import { useEffect, useMemo, useState } from "react";
import {
  FaBullhorn,
  FaCalendarAlt,
  FaCamera,
  FaCode,
  FaCoins,
  FaCrown,
  FaEnvelope,
  FaFacebook,
  FaGithub,
  FaHandshake,
  FaInfoCircle,
  FaLinkedin,
  FaMedal,
  FaPaintBrush,
  FaShareAlt,
  FaStar,
  FaTimes,
  FaUserShield,
  FaUserTie,
  FaVenus,
  FaUsers,
} from "react-icons/fa";
import { useGetCommitteeQuery } from "../../features/api/appApi";

const getInitials = (name) =>
  String(name || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase())
    .join("") || "NA";

const FormalAvatar = ({ name }) => (
  <div className="flex h-full w-full flex-col items-center justify-center bg-slate-200 text-slate-700">
    <div className="mb-3 flex h-16 w-16 items-center justify-center rounded-full bg-slate-300 text-lg font-bold">
      {getInitials(name)}
    </div>
    <div className="flex items-center gap-2 text-sm font-semibold">
      <FaUserTie className="text-base" />
      <span>Official Member</span>
    </div>
  </div>
);

const MemberPhoto = ({ member, className = "h-full w-full object-contain object-center" }) => {
  const [hasError, setHasError] = useState(false);
  const src = member?.person?.image || member?.person?.imageUrl || "";

  if (!src || hasError) {
    return <FormalAvatar name={member?.person?.name} />;
  }

  return (
    <img
      src={src}
      alt={member?.person?.name || "Committee member"}
      className={className}
      onError={() => setHasError(true)}
    />
  );
};

const CommitteePage = () => {
  const { data: committee = [] } = useGetCommitteeQuery();
  const [selectedYearId, setSelectedYearId] = useState(null);
  const [selectedMember, setSelectedMember] = useState(null);

  useEffect(() => {
    if (committee.length > 0 && !selectedYearId) {
      setSelectedYearId(committee[0].id);
    }
  }, [committee, selectedYearId]);

  const selectedYear =
    committee.find((year) => year.id === selectedYearId) || committee[0];

  const sortedMembers = useMemo(
    () => [...(selectedYear?.members || [])].sort((a, b) => Number(a.sortOrder || 0) - Number(b.sortOrder || 0)),
    [selectedYear]
  );
  const teacherMembers = useMemo(
    () =>
      sortedMembers.filter((member) => {
        const category = String(member?.person?.category || member?.person?.type || "").toLowerCase();
        return category === "teacher" || category === "faculty";
      }),
    [sortedMembers]
  );
  const studentMembers = useMemo(
    () =>
      sortedMembers.filter((member) => {
        const category = String(member?.person?.category || member?.person?.type || "").toLowerCase();
        return category !== "teacher" && category !== "faculty";
      }),
    [sortedMembers]
  );

  const getBadgeIcon = (badge) => {
    const key = String(badge || "").toLowerCase();
    const iconMap = {
      moderator: <FaUserShield className="text-rose-500" />,
      "deputy-moderator": <FaUserTie className="text-orange-500" />,
      president: <FaCrown className="text-amber-500" />,
      "vice-president": <FaStar className="text-indigo-500" />,
      secretary: <FaMedal className="text-blue-500" />,
      treasurer: <FaCoins className="text-emerald-500" />,
      organizer: <FaCalendarAlt className="text-violet-500" />,
      event: <FaBullhorn className="text-fuchsia-500" />,
      outreach: <FaHandshake className="text-red-500" />,
      information: <FaInfoCircle className="text-cyan-500" />,
      "public relation": <FaShareAlt className="text-sky-500" />,
      "social media": <FaUsers className="text-pink-500" />,
      graphics: <FaPaintBrush className="text-lime-500" />,
      photography: <FaCamera className="text-yellow-500" />,
      developer: <FaCode className="text-teal-500" />,
      "female mentor": <FaVenus className="text-purple-500" />,
      executive: <FaUserTie className="text-slate-600" />,
    };

    return iconMap[key] || <FaStar style={{ color: "var(--muted)" }} />;
  };

  const isUsableLink = (value) => {
    const str = String(value || "").trim();
    return Boolean(str) && str !== "#";
  };

  const socialLinks = (person) => [
    { key: "facebook", icon: FaFacebook, href: person?.facebook, label: "Facebook" },
    { key: "email", icon: FaEnvelope, href: person?.email ? `mailto:${person.email}` : "", label: "Email" },
    { key: "github", icon: FaGithub, href: person?.github, label: "GitHub" },
    { key: "linkedin", icon: FaLinkedin, href: person?.linkedin, label: "LinkedIn" },
  ].filter((item) => isUsableLink(item.href));

  const closeModal = () => setSelectedMember(null);

  const renderMemberGrid = (members) => (
    <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-3">
      {members.map((member) => (
        <article
          key={member.id}
          role="button"
          tabIndex={0}
          onClick={() => setSelectedMember(member)}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              setSelectedMember(member);
            }
          }}
          className="home-card group cursor-pointer overflow-hidden transition-transform duration-300 hover:-translate-y-1"
        >
          <div className="relative h-72 w-full bg-slate-100">
            <MemberPhoto member={member} />
            <div
              className="absolute right-3 top-3 rounded-full p-2 shadow"
              style={{ background: "color-mix(in srgb, var(--surface) 92%, transparent)" }}
            >
              {getBadgeIcon(member.badge || member.position)}
            </div>
          </div>

          <div className="space-y-3 p-4">
            <div>
              <h2 className="text-lg font-bold" style={{ color: "var(--text)" }}>
                {member.person?.name}
              </h2>
              <p className="text-sm font-semibold home-accent">{member.position}</p>
            </div>
            <p className="text-xs home-muted">
              {member.person?.studentId ? `ID: ${member.person.studentId}` : "No ID available"}
            </p>
            <div className="flex items-center gap-2 text-sm">
              {socialLinks(member.person).map((item) => (
                <a
                  key={item.key}
                  href={item.href}
                  target={item.key === "email" ? undefined : "_blank"}
                  rel={item.key === "email" ? undefined : "noreferrer"}
                  onClick={(e) => e.stopPropagation()}
                  className="home-pill rounded-full p-2 transition hover:opacity-85"
                  aria-label={item.label}
                >
                  <item.icon />
                </a>
              ))}
            </div>
          </div>
        </article>
      ))}
    </div>
  );

  return (
    <main className="home-shell min-h-screen px-4 pb-14 pt-28 sm:px-6">
      <section className="home-section border-none px-0 py-0">
        <div className="home-container">
          <div className="home-card-strong p-8 sm:p-10">
            <span className="section-kicker">Committee</span>
            <h1 className="home-title mt-4 text-3xl font-bold sm:text-5xl">
              GUBCPC <span className="home-accent-gradient">Leadership</span>
            </h1>
            <p className="mt-4 max-w-3xl text-sm sm:text-base home-muted">
              Committee members are shown year by year, with full historical continuity.
            </p>
          </div>
        </div>
      </section>

      <section className="home-section border-none px-0 pt-8">
        <div className="home-container">
          {committee.length === 0 ? (
            <div className="home-card p-8 text-center text-sm home-muted">
              No committee data available.
            </div>
          ) : (
            <>
              <div className="mb-4 flex flex-wrap gap-2">
                {committee.map((year) => (
                  <button
                    key={year.id}
                    onClick={() => setSelectedYearId(year.id)}
                    className={`rounded-full px-4 py-2 text-sm font-semibold ${
                      selectedYear?.id === year.id
                        ? "home-btn-primary"
                        : "home-btn-secondary"
                    }`}
                  >
                    {year.label || year.year}
                  </button>
                ))}
              </div>

              {teacherMembers.length ? (
                <div>
                  <h2 className="mb-3 text-xl font-bold">Teachers</h2>
                  {renderMemberGrid(teacherMembers)}
                </div>
              ) : null}

              {studentMembers.length ? (
                <div className={teacherMembers.length ? "mt-8" : ""}>
                  <h2 className="mb-3 text-xl font-bold">Students</h2>
                  {renderMemberGrid(studentMembers)}
                </div>
              ) : null}
            </>
          )}
        </div>
      </section>

      {selectedMember ? (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm"
          onClick={closeModal}
        >
          <div
            className="home-card-strong w-full max-w-3xl overflow-hidden p-0"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="relative grid gap-0 md:grid-cols-2">
              <button
                onClick={closeModal}
                className="absolute right-3 top-3 z-10 rounded-full bg-slate-900/80 p-2 text-white"
                aria-label="Close details"
              >
                <FaTimes />
              </button>

              <div className="h-72 bg-slate-100 md:h-full">
                <MemberPhoto member={selectedMember} />
              </div>

              <div className="space-y-4 p-6">
                <div>
                  <h3 className="text-2xl font-bold" style={{ color: "var(--text)" }}>
                    {selectedMember.person?.name}
                  </h3>
                  <p className="text-base font-semibold home-accent">{selectedMember.position}</p>
                  
                  <p className="mt-1 text-sm home-muted">
                    {selectedMember.person?.studentId || selectedMember.person?.designation || "N/A"}
                  </p>
                </div>

                <div>
                  <p className="mb-2 text-sm font-semibold" style={{ color: "var(--text)" }}>
                    Connect
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {socialLinks(selectedMember.person).map((item) => (
                      <a
                        key={item.key}
                        href={item.href}
                        target={item.key === "email" ? undefined : "_blank"}
                        rel={item.key === "email" ? undefined : "noreferrer"}
                        className="home-pill inline-flex items-center gap-2 rounded-full px-3 py-1.5 text-sm transition hover:opacity-85"
                      >
                        <item.icon />
                        <span>{item.label}</span>
                      </a>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </main>
  );
};

export default CommitteePage;